import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { Brand } from '../components/Brand';
import { Button } from '../components/ui';
import NotFoundPage from './NotFoundPage';

const RELOAD_KEY = 'sk.chunkReloadAt';

/** True when an error comes from a lazy route chunk that could not be downloaded. */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name} ${error.message}` : String(error);
  return /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk|module script/i.test(
    message,
  );
}

/**
 * Reloads the page to fetch fresh chunks, at most once per 30 s (avoids reload loops while offline).
 * Returns true when a reload was started.
 */
export function reloadOnceForStaleChunks(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < 30_000) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export default function RouteErrorPage() {
  const error = useRouteError();
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;

  const chunk = isChunkLoadError(error);
  if (chunk && reloadOnceForStaleChunks()) return null;
  if (!chunk) console.error(error);

  return (
    <main className="bg-stage theme-light flex min-h-dvh flex-col items-center justify-center gap-6 p-8 text-center text-white">
      <Brand tone="dark" />
      <h1 className="text-3xl font-extrabold">{chunk ? 'Connection problem' : 'Something went wrong'}</h1>
      <p className="max-w-md text-white/80">
        {chunk
          ? "Part of the app couldn't be downloaded. Check your connection and try again; if you were in a game, you'll rejoin automatically."
          : 'An unexpected error occurred. Reloading usually fixes it; if you were in a game, you will rejoin automatically.'}
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <Button variant="white" size="lg" onClick={() => window.location.reload()}>
          Reload
        </Button>
        <Link
          to="/"
          className="inline-flex h-12 items-center rounded-xl px-6 font-bold text-white ring-1 ring-white/40"
        >
          Home
        </Link>
      </div>
    </main>
  );
}
